// Global DOM components.
import React, { Component } from 'react'
import { Route, withRouter } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import { createTheme, Grid, ThemeProvider } from '@mui/material'
import * as locationSearch from 'query-string'
import ReactGA from 'react-ga4'

// Helpers.
import _ from 'lodash'

// Redux.
import { connect } from 'react-redux'
import { toggleLeftMenu } from 'store/actions'
import { changeSearchText, updateSearchResults } from 'store/actions/search'

// Custom global components.
import LeftMenu from './global/drawer/LeftMenu'
import HeaderBar from './global/header/HeaderBar'
import Banner from './global/banner'
import SearchForm from 'components/SearchForm'
import AuthRoute from 'components/AuthRoute'
import SessionTimeoutModal from 'components/SessionTimeoutModal'

// Custom Pages.
import Login from '../pages/login/Login'

// URL paths.
import {
  BASE_PAGE,
  SEARCH_PAGE,
  PROFILE_PAGE,
  PIPELINE_PAGE,
  GIFT_PAGE,
  CONTACT_PAGE,
  LOGIN,
  USER_PROFILE,
  RESET_PASSWORD
} from '../utils/paths'

// App Language.
import { checkIpRange, keepSessionAlive, redirectTo, updateSessionStatus } from 'store/actions/user'
import { getAllFacetsAndSyncURL } from 'store/actions/facets'
import { withTranslation } from 'react-i18next'

// Rebuilt Pages
import SearchPage from 'pages/search'
import ProfilePage from 'pages/profile'
import PipelinePage from 'pages/pipeline'
import GiftPage from 'pages/gift'
import ContactPage from 'pages/contact'
import PasswordResetPage from 'pages/password'
import UserProfilePage from 'pages/user_profile'
import { getFrontendSettings } from 'store/actions/frontendSettings'

ReactGA.initialize('G-0PJB5KMFL2')

const theme = createTheme({})

// Check if there is any search on the URL.
const queryStrings = locationSearch.parse(window.location.search)

let searchText = {
  value: typeof queryStrings.search !== 'undefined' ? queryStrings.search : ''
}

let timer

const FRENCH_DOMAIN = 'connexionsubvention'

class App extends Component {
  state = {
    searchAutocompleteData: [],
    initSearchText: ''
  }

  componentWillMount() {
    const { i18n } = this.props
    const { hostname } = window.location

    const lang = localStorage.getItem('lang') || 'en'
    i18n.changeLanguage(lang)

    const language = i18n.language

    const path = window.location.pathname.split('/')
    let first_path = ''
    if (path[1] == 'fr') {
      first_path = '/' + path[2]
    } else {
      first_path = '/' + path[1]
    }

    // Adding language to main div.
    document.getElementById('root').className = language
    document.getElementsByTagName('body')[0].className = language
    this.updateLoginClass()
  }

  /* Search handlers */
  componentDidMount() {
    const {
      boundChangeSearchText,
      location,
      keepSessionAlive,
      checkIpRangeAuth,
      updateSessionStatus,
      isAuthenticated,
      redirectTo,
      getAllFacetsAndSyncURL
    } = this.props

    // Start interval to ping API to keep session alive
    keepSessionAlive()

    // Check if user is authenticated based on IP range
    if (!isAuthenticated) {
      if (this.props.location.pathname !== RESET_PASSWORD) {
        checkIpRangeAuth()
      }
    } else {
      updateSessionStatus()
      // if base route or /login, redirect to /search
      if (this.props.location.pathname === BASE_PAGE || this.props.location.pathname === LOGIN) {
        redirectTo('/search', { replace: true })
      }
    }

    document.title = this.props.t('global.title')
    // Change the search text if there is any keyword.
    if (location.pathname === SEARCH_PAGE && searchText.value !== '') {
      boundChangeSearchText(searchText.value)
    }

    if (isAuthenticated) {
      // read URL params and set correct filter and pagination settings
      const [_, params] = this.props.location.search.split('?')

      // fetch facet trees and once all succeed, sync url with redux store
      getAllFacetsAndSyncURL(params, this.props.location.pathname)
    }
  }

  componentDidUpdate() {
    this.updateLoginClass()

    if (this.props.isAuthenticated && !this.props.haveSettingsLoaded) {
      this.props.getFrontendSettings()
    }
  }

  updateLoginClass() {
    const path = window.location.pathname.split('/')
    let first_path = ''
    if (path[1] == 'fr') {
      first_path = '/fr/' + path[2]
    } else {
      first_path = '/' + path[1]
    }
    if (window.location.pathname === LOGIN || first_path === RESET_PASSWORD) {
      document.getElementById('root').className += ` ${LOGIN.split('/').join('')}`
    } else {
      document.getElementById('root').classList.remove('login')
    }
  }

  transformToAssocArray = prmstr => {
    let params = {}
    const prmarr = prmstr.split('&')
    for (let i = 0; i < prmarr.length; i++) {
      var tmparr = prmarr[i].split('=')
      params[tmparr[0]] = tmparr[1]
    }
    return params
  }

  gotoFunder = funderId => {
    this.props.history.push(`${PROFILE_PAGE}/${funderId}`)
  }

  render() {
    return (
      <ThemeProvider theme={theme}>
        <div className="App">
          <LeftMenu
            onCloseIconButtonClick={this.props.toggleLeftMenu}
            showMenu={this.props.leftMenu.opened}
          />
          <header className="App-header">
            <HeaderBar
              history={this.props.history}
              onLeftIconButtonClick={this.props.toggleLeftMenu}
            />
            {this.props.isAuthenticated && (
              <>
                <MediaQuery query="(min-width: 768px)">
                  <SearchForm gotoFunder={this.gotoFunder} />
                </MediaQuery>
                <MediaQuery query="(max-width: 767px)">
                  <SearchForm gotoFunder={this.gotoFunder} mobile={true} />
                </MediaQuery>
                <Banner />
              </>
            )}
          </header>

          <SessionTimeoutModal />

          <Grid sx={{ justifyContent: 'space-between' }}>
            <Route path={LOGIN} exact component={Login} />
            <Route path={RESET_PASSWORD} exact component={PasswordResetPage} />
            <AuthRoute path={BASE_PAGE} exact component={SearchPage} />
            <AuthRoute path={SEARCH_PAGE} exact component={SearchPage} />
            <AuthRoute path={`${PROFILE_PAGE}/:profileId/:tab?/:extra*`} component={ProfilePage} />
            <AuthRoute path={`${USER_PROFILE}/:tab?`} component={UserProfilePage} />
            <AuthRoute path={PIPELINE_PAGE} exact component={PipelinePage} />
            <AuthRoute path={GIFT_PAGE} exact component={GiftPage} />
            <AuthRoute path={CONTACT_PAGE} exact component={ContactPage} />
          </Grid>
        </div>
      </ThemeProvider>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  leftMenu: state.leftMenu,
  isAuthenticated: state.user.isAuthenticated,
  isLibraryMode: state.user.isLibraryMode,
  haveSettingsLoaded: state.frontendSettings.hasLoaded
})

const mapDispatchToProps = dispatch => ({
  getFrontendSettings: () => dispatch(getFrontendSettings),
  toggleLeftMenu: () => dispatch(toggleLeftMenu()),
  boundChangeSearchText: searchText => dispatch(changeSearchText(searchText)),
  changeSearchResults: (searchResults, searchFacets, pagerInfo) =>
    dispatch(updateSearchResults(searchResults, searchFacets, pagerInfo)),
  updateRegion: (region, Default = null) => {
    dispatch({ type: 'CHANGE_SEARCH_REGION', value: region, default: Default })
  },
  redirectTo: (path, options) => {
    dispatch(redirectTo(path, options))
  },
  clearSearchText: () => dispatch(changeSearchText('')),
  keepSessionAlive: () => dispatch(keepSessionAlive()),
  checkIpRangeAuth: () => dispatch(checkIpRange()),
  updateSessionStatus: () => dispatch(updateSessionStatus()),
  getAllFacetsAndSyncURL: (params, pathname) => dispatch(getAllFacetsAndSyncURL(params, pathname))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(App)))
